// Google · Google Ads — conexión de la cuenta de la org, customer ID y prueba
// de salud. Reemplaza la pantalla vieja de «Marketing — Google Ads»
// (decía «Calaita» y usaba colores fijos). Al volver de Google, /oauth-complete
// redirige aquí.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Link2, LogOut, RefreshCw, Save, Search } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import {
  ApiError, disconnectAuth, getAuthStatus, runHealthCheck, startOAuth, updateAuth,
  type HealthOut, type OAuthStatus,
} from '@/lib/api_google_ads';
import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '@/modules/orion-shell/ui';

import { Aviso, Insignia } from './shared';

const msg = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
const cidFrom = (rn: string) => rn.replace(/^customers\//, '');
const conGuiones = (cid: string) => (/^\d{10}$/.test(cid) ? `${cid.slice(0, 3)}-${cid.slice(3, 6)}-${cid.slice(6)}` : cid);

/** Mensajes de Google traducidos a algo accionable. */
function humano(error: string): string {
  if (/invalid_grant|rejected the refresh_token|revoked/i.test(error)) return 'Google retiró el permiso. Desconecta y vuelve a conectar.';
  if (/DEVELOPER_TOKEN|developer token/i.test(error)) return 'El token de desarrollador de Google Ads no está aprobado para esta cuenta. Lo resuelve el equipo técnico.';
  if (/PERMISSION_DENIED|USER_PERMISSION_DENIED/i.test(error)) return 'El usuario de Google que conectaste no tiene acceso a esa cuenta de Google Ads.';
  return error;
}

export { conGuiones };

export function GoogleAdsView({ addNotification }: { addNotification?: (n: { type: string; title: string; message?: string }) => void }) {
  const { isEnabled } = useModules();
  const conMarketing = isEnabled('marketing');
  const [status, setStatus] = useState<OAuthStatus | null>(null);
  const [health, setHealth] = useState<HealthOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'' | 'connect' | 'disconnect' | 'health' | 'save'>('');
  const [cid, setCid] = useState('');
  const [mcc, setMcc] = useState('');
  const [aviso, setAviso] = useState<{ tono: 'ok' | 'error'; texto: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const s = await getAuthStatus();
      setStatus(s);
      setCid(s.google_customer_id);
      setMcc(s.login_customer_id);
      setHealth(s.connected ? await runHealthCheck().catch((e) => ({ ok: false, accessible_customers: [], error: msg(e), status: null })) : null);
    } catch (e) {
      setError(msg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (conMarketing) void load();
    else setLoading(false);
  }, [load, conMarketing]);

  async function conectar() {
    setBusy('connect');
    try {
      const out = await startOAuth();
      window.location.href = out.authorization_url; // Google no acepta ventanas emergentes para el consentimiento.
    } catch (e) {
      setBusy('');
      setAviso({ tono: 'error', texto: msg(e) });
    }
  }

  async function desconectar() {
    if (!window.confirm('¿Desconectar Google Ads? Tendrás que volver a autorizar.')) return;
    setBusy('disconnect');
    try {
      await disconnectAuth();
      setAviso({ tono: 'ok', texto: 'Google Ads desconectado.' });
      await load();
    } catch (e) {
      setAviso({ tono: 'error', texto: msg(e) });
    } finally {
      setBusy('');
    }
  }

  async function guardar() {
    setBusy('save');
    try {
      await updateAuth({ google_customer_id: cid.replace(/-/g, '').trim(), login_customer_id: mcc.replace(/-/g, '').trim() });
      addNotification?.({ type: 'success', title: 'Cuenta de Google Ads guardada' });
      setAviso({ tono: 'ok', texto: 'Cuenta guardada.' });
      await load();
    } catch (e) {
      setAviso({ tono: 'error', texto: msg(e) });
    } finally {
      setBusy('');
    }
  }

  async function probar() {
    setBusy('health');
    try {
      setHealth(await runHealthCheck());
    } catch (e) {
      setHealth({ ok: false, accessible_customers: [], error: msg(e), status: null });
    } finally {
      setBusy('');
    }
  }

  if (!conMarketing) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
        <PageHeader title="Google Ads" />
        <Card><EmptyState title="Tu organización no tiene el módulo de Marketing">Google Ads forma parte de Marketing. Pídele al administrador que lo active.</EmptyState></Card>
      </div>
    );
  }

  const cuentaFuera = !!health?.ok && !!status?.google_customer_id
    && !health.accessible_customers.some((rn) => cidFrom(rn) === status?.google_customer_id);
  const conProblemas = (!!health && !health.ok) || cuentaFuera;
  const sinCambios = !!status && cid.replace(/-/g, '') === status.google_customer_id && mcc.replace(/-/g, '') === status.login_customer_id;

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Google Ads"
        subtitle="Conecta la cuenta de Google Ads de tu organización para crear campañas y medir su gasto."
        actions={<Button variant="ghost" onClick={() => void load()} aria-label="Actualizar"><RefreshCw size={16} aria-hidden /></Button>}
      />
      {aviso && <Aviso tono={aviso.tono} onClose={() => setAviso(null)}>{aviso.texto}</Aviso>}

      {loading ? <Loading /> : error ? (
        <div className="space-y-3"><ErrorNote>{error}</ErrorNote><Button onClick={() => void load()}>Reintentar</Button></div>
      ) : !status?.connected ? (
        <Card>
          <EmptyState
            icon={<Link2 size={18} />}
            title="Google Ads no está conectado"
            action={<Button variant="brand" className="mt-2" onClick={() => void conectar()} disabled={busy === 'connect'}><Link2 size={16} aria-hidden /> {busy === 'connect' ? 'Abriendo Google…' : 'Conectar con Google'}</Button>}
          >
            Entra con el usuario de Google que administra tu cuenta de Google Ads. Si Google te pide verificar tu identidad (contraseña o llave de acceso), termina ese paso: si lo cancelas, la conexión no se completa.
          </EmptyState>
          {status?.error_message && <div className="mt-3"><ErrorNote>{humano(status.error_message)}</ErrorNote></div>}
        </Card>
      ) : (
        <div className="space-y-4">
          <Card title="Conexión" action={<Insignia tono={conProblemas ? 'aviso' : 'ok'}>{conProblemas ? 'Con problemas' : 'Conectado'}</Insignia>}>
            <p className="text-xs text-muted-foreground">
              Desde {status.connected_at ? new Date(status.connected_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
              {status.last_used_at && <> · Último uso {new Date(status.last_used_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</>}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-muted-foreground">ID de la cuenta (Customer ID)
                <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="123-456-7890" inputMode="numeric"
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm text-foreground" />
              </label>
              <label className="text-sm text-muted-foreground">Cuenta administradora (MCC, opcional)
                <input value={mcc} onChange={(e) => setMcc(e.target.value)} placeholder="Opcional" inputMode="numeric"
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm text-foreground" />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap justify-between gap-2">
              <Button variant="danger" onClick={() => void desconectar()} disabled={busy === 'disconnect'}><LogOut size={14} aria-hidden /> Desconectar</Button>
              <Button variant="brand" onClick={() => void guardar()} disabled={busy === 'save' || sinCambios}><Save size={16} aria-hidden /> Guardar cuenta</Button>
            </div>
          </Card>

          <Card title="Prueba de conexión" action={<Button variant="ghost" onClick={() => void probar()} disabled={busy === 'health'}><Search size={14} aria-hidden /> Probar ahora</Button>}>
            {!health ? <p className="text-sm text-muted-foreground">Comprueba que el permiso sigue vigente y qué cuentas puede ver.</p> : health.ok ? (
              <>
                <p className="flex items-center gap-2 text-sm text-foreground"><CheckCircle2 size={16} className="text-primary" aria-hidden /> El permiso funciona.</p>
                {health.accessible_customers.length > 0 ? (
                  <>
                    <p className="mt-3 text-xs text-muted-foreground">Cuentas que puede ver este usuario de Google (toca una para usarla):</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {health.accessible_customers.map((rn) => (
                        <button key={rn} type="button" onClick={() => setCid(cidFrom(rn))}
                          className={`rounded-full px-2.5 py-1 font-mono text-xs transition-colors ${cidFrom(rn) === cid.replace(/-/g, '') ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/70'}`}>
                          {conGuiones(cidFrom(rn))}
                        </button>
                      ))}
                    </div>
                    {cid && !health.accessible_customers.some((rn) => cidFrom(rn) === cid.replace(/-/g, '')) && (
                      <p className="mt-3 text-xs text-destructive">La cuenta {conGuiones(cid.replace(/-/g, ''))} no está en la lista: conectaste con otro usuario de Google. Desconecta y entra con el que administra esa cuenta.</p>
                    )}
                  </>
                ) : <p className="mt-2 text-xs text-muted-foreground">Este usuario de Google no administra ninguna cuenta de Google Ads.</p>}
              </>
            ) : <ErrorNote>{humano(health.error || 'Error desconocido')}</ErrorNote>}
          </Card>

          <Link to="/m/mkt-google/campaigns" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            Ver campañas de Google Ads <ExternalLink size={14} aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}
